import useSWR from 'swr'
import { useAppStore } from '@forwardflow/store/appStore'
import {
   gated,
   isFreeFlow,
} from '@forwardflow/features/campaign-v2/utils/demo-mode'
import { campaignV2Api } from '../services'
import { ICriteria, TContentKind } from '../interfaces/campaign-v2.types'

const K = 'CAMPAIGN_V2'

/** Demo data is fine without an org; the live BFF is not. */
const useKey = (parts: unknown[] | null): unknown[] | null => {
   const org = useAppStore((s) => s.currentOrganization)
   return !parts
      ? null
      : isFreeFlow()
        ? [...parts, 'demo']
        : org
          ? gated([...parts, org])
          : null
}

export const useV2Readiness = () =>
   useSWR(useKey([K, 'readiness']), () => campaignV2Api().readiness(), {
      shouldRetryOnError: false,
   })

export const useBusinessCategories = () =>
   useSWR(
      useKey([K, 'categories']),
      () => campaignV2Api().businessCategories(),
      {
         revalidateOnFocus: false,
      }
   )

export const useCatalog = (kind: TContentKind, categoryId: number | null) =>
   useSWR(
      useKey(categoryId ? [K, 'catalog', kind, categoryId] : null),
      () => campaignV2Api().catalog(kind, categoryId as number),
      { revalidateOnFocus: false }
   )

export const useVoices = () =>
   useSWR(useKey([K, 'voices']), () => campaignV2Api().voices(), {
      revalidateOnFocus: false,
   })

export const useAgents = () =>
   useSWR(useKey([K, 'agents']), () => campaignV2Api().agents(), {
      revalidateOnFocus: false,
   })

export const useLiveCampaign = (campaignId: string | null) =>
   useSWR(useKey(campaignId ? [K, 'live', campaignId] : null), () =>
      campaignV2Api().readCampaign(campaignId as string)
   )

export const usePreview = (campaignId: string | null, revision: number) =>
   useSWR(
      useKey(campaignId ? [K, 'preview', campaignId, revision] : null),
      () => campaignV2Api().preview(campaignId as string),
      { revalidateOnFocus: false }
   )

export const useInventoryNumbers = () =>
   useSWR(useKey([K, 'inventory']), () => campaignV2Api().inventoryNumbers())

export const useAvailableNumbers = (search: string | null) =>
   useSWR(
      useKey(search === null ? null : [K, 'available', search]),
      () => campaignV2Api().availableNumbers(search || ''),
      { revalidateOnFocus: false, shouldRetryOnError: false }
   )

export const useNumberPricing = (total: number) =>
   useSWR(
      useKey(total > 0 ? [K, 'pricing', total] : null),
      () => campaignV2Api().pricing(total),
      {
         revalidateOnFocus: false,
      }
   )

export const useOrgAnswering = () =>
   useSWR(useKey([K, 'answering']), () => campaignV2Api().orgAnswering(), {
      revalidateOnFocus: false,
   })

export const useEstimatedLeads = (criteria: ICriteria[] | null) =>
   useSWR(
      useKey(criteria ? [K, 'estimate', JSON.stringify(criteria)] : null),
      () => campaignV2Api().estimatedLeads(criteria as ICriteria[]),
      { revalidateOnFocus: false, dedupingInterval: 5000 }
   )
